import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Button } from 'components/ui/button';
import { Alert, Badge, Form, Modal, Spinner } from 'react-bootstrap-5';

import { saveSearch } from 'bundles/connect/components/store/actions/results';
import { showSaveSearchModal } from 'bundles/connect/components/store/actions/ui';
import CriteriaDecorator from '../../helpers/criteria_decorator';
import { displayToast } from '../../../../helpers/display_toast';
import { offMarketPropertySearch, profileNotificationUrl, savedSearchesUrl } from '../../../../helpers/prospect_api';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBellSlash, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';
import type { Criteria } from 'bundles/connect/types';

type Props = {
  criteria: Criteria;
  offMarketSearch: boolean;
  saveSearch: (name: string, ids: string[] | null) => Promise<void>;
  show: boolean;
  showSaveSearchModal: (show: boolean) => void;
  totalOffMarketResults: number;
};

function SaveSearchModal(
  { criteria, offMarketSearch, saveSearch, show, showSaveSearchModal, totalOffMarketResults }: Props
) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const decoratedCriteria = new CriteriaDecorator(criteria, offMarketSearch);

  const assembleInsightsPropertyIds = async () => {
    let ids = null;
    if (criteria && offMarketSearch) {
      const results = await offMarketPropertySearch(criteria, 10_000);
      if (results.total <= 10_000) {
        ids = (results.hits || []).map(hit => hit.propertyId);
      }
    }
    return ids;
  };

  const save = () => {
    setSaving(true);

    (async () => {
      const ids = await assembleInsightsPropertyIds();

      saveSearch(name || decoratedCriteria.name(), ids).then(() => {
        setSaving(false);
        displayToast('Your search has been saved!', 'success');
        showSaveSearchModal(false);
        setName('');
      });
    })();
  };

  return (
    <Modal show={show} onHide={() => showSaveSearchModal(false)}>
      <Modal.Header className="p-3 flex-row" closeButton>
        <Modal.Title>
          <h4 className="mb-0">Save Search</h4>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <div className="d-flex flex-column gap-3">
          <div>
            You can save your search and enable notifications for all your saved searches
            to get updates when new properties are found.
          </div>
          {!isEmpty(decoratedCriteria.filterSummary()) && (
            <div>
              Please confirm these are the filters you wish to save:
              <div className="d-flex gap-1 flex-wrap">
                {decoratedCriteria.filterSummary().slice(0, 2).map((attr, idx) => (
                  <Badge
                    bg=""
                    className="px-1 py-0 d-flex align-items-center fs-x-small border badge-muted badge-secondary"
                    key={idx}
                    style={{ minWidth: 0 }}
                  >
                    <span className="text-truncate">
                      {attr.label}
                    </span>
                  </Badge>
                ))}

                {decoratedCriteria.filterSummary().length > 2 && (
                  <Badge
                    bg=""
                    className="px-1 py-0 d-flex align-items-center fs-x-small badge-muted badge-secondary border"
                  >
                    {decoratedCriteria.filterSummary().length - 2} more
                  </Badge>
                )}
              </div>
            </div>
          )}
          <Form.Group>
            <Form.Label className="mt-2">
              Name for this search
            </Form.Label>
            <Form.Control
              maxLength={150}
              placeholder={decoratedCriteria.name()}
              value={name || ''}
              onChange={e => setName(e.target.value)}
            />
          </Form.Group>
          <div>
            Manage saved searches notifications inside your
            <a href={profileNotificationUrl} rel="noopener noreferrer" target="_blank"> account settings</a>.
          </div>
          {offMarketSearch && totalOffMarketResults > 10_000 && (
            <Alert className="p-3 mb-0" variant="info">
              <div className="d-flex align-items-start gap-2">
                <FontAwesomeIcon className="mt-1" icon={faBellSlash} />
                Search terms are too broad for this search causing results to be over 10,000.
                You can still save the search, but you will not receive notifications when new properties get added.
              </div>
            </Alert>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="d-flex align-items-center justify-content-between p-2">
        <Button
          className="m-0"
          href={savedSearchesUrl}
          label="View saved searches"
          rel="noopener noreferrer"
          target="_blank"
          variant="ghost"
        />

        <div className="d-flex gap-2 m-0">
          <Button label="Cancel" variant="ghost" onClick={() => showSaveSearchModal(false)} />
          <Button
            disabled={saving}
            id={offMarketSearch ? 'off_market_save_search' : 'exclusive_listing_save_search'}
            onClick={save}
          >
            {saving && <Spinner animation="border" size="sm" />}
            {!saving && <FontAwesomeIcon icon={faFloppyDisk} />}
            Save Search
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

const mapStateToProps = ({ criteria, ui, results }) => {
  return {
    criteria,
    show: ui.showSaveSearchModal,
    offMarketSearch: ui.offMarketSearch,
    totalOffMarketResults: results.totalOffMarketResults
  };
};

export default connect(mapStateToProps, { saveSearch, showSaveSearchModal })(SaveSearchModal);
